import { useNavigate } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { toast } from "@volca/ui/lib/toast";
import { useArchiveReport } from "@/profitability/report-query";
import { PROFITABILITY_PATH } from "@/profitability/routes";
import { SETTINGS_CONTROLS } from "@/profitability/settings/control-classes";
import type { ProfitabilityReport } from "@/profitability/types";
import { reportError } from "@/profitability/utils";

/**
 * Archives a template (RPT-008): its published results, findings and
 * feedback are the record of what the business stood behind, so a template
 * is put away rather than deleted, and can be restored from the list.
 */
export const ArchiveTemplateDialog = ({
  businessId,
  report,
  open,
  onOpenChange,
}: {
  businessId: string;
  report: ProfitabilityReport;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const navigate = useNavigate();
  const archive = useArchiveReport(businessId);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!archive.isPending) onOpenChange(next);
      }}
    >
      <DialogContent className={SETTINGS_CONTROLS}>
        <DialogHeader>
          <DialogTitle>Archive {report.name}?</DialogTitle>
          <DialogDescription>
            Archived agents are hidden from the active list and can’t run new
            reports. Past reports, findings and saved versions remain available.
            You can restore this agent later.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="secondary"
            disabled={archive.isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            loading={archive.isPending}
            onClick={() =>
              archive.mutate(report.id, {
                onSuccess: () => {
                  toast.success(`${report.name} was archived`);
                  navigate(PROFITABILITY_PATH);
                },
                onError: (error) =>
                  toast.error("The agent could not be archived", {
                    description: reportError(error),
                  }),
              })
            }
          >
            Archive agent
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
